import React, {useMemo} from 'react';
import {
  AbsoluteFill,
  Audio,
  OffthreadVideo,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {parseMedia} from '@remotion/media-parser';
import {webReader} from '@remotion/media-parser/web';
import type {CalculateMetadataFunction} from 'remotion';
import {
  FocusAreaMarker,
  HighlightOverlay,
} from '../../../components/markers';
import {
  getHighlightEndTime,
  isHighlightActiveAtTime,
} from './highlightTiming';
import type {HighlightItem, VideoHighlightProps} from './types';

const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;
const DEFAULT_FPS = 30;
const MIN_DURATION_SECONDS = 5;

const resolveVideoSrc = (src: string): string => {
  if (/^(https?:)?\/\//.test(src) || src.startsWith('data:')) {
    return src;
  }

  // Avoid resolving an already-static Remotion asset path twice.
  if (src.startsWith('/static-')) {
    return src;
  }

  if (src.startsWith('/public/') || src.startsWith('public/')) {
    return src;
  }

  if (src.startsWith('/')) {
    return staticFile(src.slice(1));
  }

  return src;
};

async function getVideoMeta(src: string): Promise<{
  duration: number;
  width: number;
  height: number;
  fps: number;
}> {
  const result = await parseMedia({
    src,
    fields: {durationInSeconds: true, dimensions: true, fps: true},
    reader: webReader,
  });

  return {
    duration: result.durationInSeconds ?? 0,
    width: result.dimensions?.width ?? 0,
    height: result.dimensions?.height ?? 0,
    fps: result.fps ?? 0,
  };
}

export const calculateVideoHighlightMetadata: CalculateMetadataFunction<
  VideoHighlightProps
> = async ({props}) => {
  const backgroundVideoSrc = resolveVideoSrc(props.backgroundVideo.url);
  const hasCompleteBackgroundVideoMeta =
    (props.backgroundVideo.width ?? 0) > 0 &&
    (props.backgroundVideo.height ?? 0) > 0 &&
    (props.backgroundVideo.fps ?? 0) > 0 &&
    (props.backgroundVideo.duration ?? 0) > 0;

  let parsedVideoMeta: Awaited<ReturnType<typeof getVideoMeta>> | null = null;

  if (!hasCompleteBackgroundVideoMeta) {
    try {
      parsedVideoMeta = await getVideoMeta(backgroundVideoSrc);
    } catch (err) {
      console.warn(
        `Failed to parse background video metadata for ${props.backgroundVideo.url}:`,
        err,
      );
    }
  }

  const width =
    props.videoConfig?.width ||
    parsedVideoMeta?.width ||
    props.backgroundVideo.width ||
    DEFAULT_WIDTH;
  const height =
    props.videoConfig?.height ||
    parsedVideoMeta?.height ||
    props.backgroundVideo.height ||
    DEFAULT_HEIGHT;
  const rawFps =
    props.videoConfig?.fps ||
    parsedVideoMeta?.fps ||
    props.backgroundVideo.fps ||
    DEFAULT_FPS;
  const fps = rawFps > 0 ? Math.round(rawFps) : DEFAULT_FPS;

  const fallbackDuration = Math.max(
    ...props.highlights.map((highlight) => getHighlightEndTime(highlight)),
    MIN_DURATION_SECONDS,
  );
  const rawDuration =
    props.backgroundVideo.duration ||
    parsedVideoMeta?.duration ||
    fallbackDuration;
  const durationInFrames = Math.max(1, Math.ceil(rawDuration * fps));

  return {
    width,
    height,
    fps,
    durationInFrames,
    props: {
      ...props,
      backgroundVideo: {
        ...props.backgroundVideo,
        url: backgroundVideoSrc,
        duration: rawDuration,
        width: parsedVideoMeta?.width || props.backgroundVideo.width,
        height: parsedVideoMeta?.height || props.backgroundVideo.height,
        fps,
      },
    },
  };
};

const findActiveFocusArea = (
  highlights: HighlightItem[],
  currentTimeInSeconds: number,
): HighlightItem | null => {
  for (let index = highlights.length - 1; index >= 0; index -= 1) {
    const highlight = highlights[index];

    if (
      highlight.type === 'focus-area' &&
      highlight.position != null &&
      isHighlightActiveAtTime(highlight, currentTimeInSeconds)
    ) {
      return highlight;
    }
  }

  return null;
};

export const VideoHighlight: React.FC<VideoHighlightProps> = ({
  backgroundVideo,
  highlights,
  defaultFadeIn = 0,
  defaultFadeOut = 0,
  originalVolume = 100,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const currentTimeInSeconds = frame / fps;

  const activeFocusArea = useMemo(() => {
    return findActiveFocusArea(highlights, currentTimeInSeconds);
  }, [currentTimeInSeconds, highlights]);

  const backgroundVideoSrc = useMemo(() => {
    return resolveVideoSrc(backgroundVideo.url);
  }, [backgroundVideo.url]);

  return (
    <AbsoluteFill style={{backgroundColor: '#000', overflow: 'hidden'}}>
      <FocusAreaMarker
        highlight={activeFocusArea}
        defaultFadeIn={defaultFadeIn}
        defaultFadeOut={defaultFadeOut}
      >
        <OffthreadVideo
          src={backgroundVideoSrc}
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </FocusAreaMarker>
      <HighlightOverlay
        highlights={highlights}
        defaultFadeIn={defaultFadeIn}
        defaultFadeOut={defaultFadeOut}
      />
      {originalVolume > 0 ? (
        <Audio src={backgroundVideoSrc} volume={originalVolume / 100} />
      ) : null}
    </AbsoluteFill>
  );
};
